/**
 * Key Controls Heat Map Component
 * 
 * Displays a matrix view of all assets and their key controls implementation status
 * with color-coded cells to quickly identify compliance gaps.
 * 
 * PRD v19: Streamlined to 6 key controls (removed RBAC, Advanced Endpoint Protection, SOC Onboarding)
 * Aligned with import/export templates and asset form structure.
 */
import React, { useState, useMemo } from 'react';

const KeyControlsHeatMap = ({ assets, onEdit }) => {
  const [sortBy, setSortBy] = useState('criticality');
  const [showDetails, setShowDetails] = useState(false);
  
  // Key controls to display in the heat map (PRD v19: Streamlined to 6 controls)
  const controls = [
    { id: 'ssoImplemented', label: 'SSO' },
    { id: 'twoFactorAuth', label: '2FA' },
    { id: 'dataEncryption', label: 'DATA ENCRYPTION' },
    { id: 'backupsImplemented', label: 'BACKUPS' },
    { id: 'networkSegmentation', label: 'NETWORK SEGMENTATION' },
    { id: 'userAccessReviews', label: 'USER ACCESS REVIEWS' }
  ];
  
  // Calculate compliance metrics
  const metrics = useMemo(() => {
    let totalControls = 0;
    let implementedControls = 0;
    let missingControls = 0;
    let naControls = 0;
    
    assets.forEach(asset => {
      controls.forEach(control => {
        const value = asset[control.id];
        if (value) {
          totalControls++;
          if (value === 'Yes') implementedControls++;
          if (value === 'No') missingControls++;
          if (value === 'N/A') naControls++;
        }
      });
    });
    
    const implementationRate = totalControls > 0 
      ? Math.round((implementedControls / (totalControls - naControls)) * 100) 
      : 0;
    
    return {
      totalControls,
      implementedControls,
      missingControls,
      naControls,
      implementationRate
    };
  }, [assets, controls]);
  
  // Sort assets based on selected sort method
  const sortedAssets = useMemo(() => {
    return [...assets].sort((a, b) => {
      if (sortBy === 'name') {
        return a.assetName.localeCompare(b.assetName);
      } else if (sortBy === 'criticality') {
        // Sort by criticality (1-Extremely first, 5-Low last)
        const getNumber = (str) => parseInt(str?.split('-')[0] || '5', 10);
        return getNumber(a.criticalityClassification) - getNumber(b.criticalityClassification);
      } else if (sortBy === 'department') {
        return a.ownerDepartment.localeCompare(b.ownerDepartment);
      } else if (sortBy === 'gaps') {
        // Sort by number of missing controls (most gaps first)
        const countGaps = (asset) => {
          return controls.reduce((count, control) => {
            return asset[control.id] === 'No' ? count + 1 : count;
          }, 0);
        };
        return countGaps(b) - countGaps(a);
      }
      return 0;
    });
  }, [assets, sortBy, controls]);
  
  // Get asset risk level based on criticality
  const getAssetRiskClass = (asset) => {
    const criticalityLevel = asset.criticalityClassification?.split('-')[0] || '5';
    
    if (criticalityLevel === '1' || criticalityLevel === '2') return 'high';
    if (criticalityLevel === '3') return 'medium';
    return 'low';
  };
  
  // Update the cell click handler function in KeyControlsHeatMap.js
  const handleCellClick = (assetId, controlKey) => {
    console.log(`Cell clicked for asset ${assetId}, control: ${controlKey}`);
    
    // Call the onEdit function passed from the parent with the assetId
    // and specify that we want the keyControls tab to open
    if (onEdit) {
      onEdit(assetId, 'keyControls');
    }
  };
  
  return (
    <div className="bg-white shadow overflow-hidden rounded-lg border border-gray-200">
      {/* Header with title and controls */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              Key Controls Implementation
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                {metrics.implementationRate}% implemented
              </span>
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Visualising security control implementation across all assets
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center">
              <span className="mr-2 text-sm text-gray-600">Sort by:</span>
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="block pl-3 pr-10 py-1.5 text-sm border-gray-300 rounded-md"
              >
                <option value="criticality">Criticality</option>
                <option value="gaps">Most Gaps</option>
                <option value="name">Asset Name</option>
                <option value="department">Department</option>
              </select>
            </div>
            
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Summary stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 border-b border-gray-200">
        <div className="px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            IMPLEMENTATION RATE
          </div>
          <div className="mt-1 flex items-baseline">
            <div className="text-2xl font-semibold text-blue-600">
              {metrics.implementationRate}%
            </div>
            <div className="ml-2 text-sm text-gray-600">
              of applicable controls
            </div>
          </div>
        </div>
        
        <div className="px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            IMPLEMENTED CONTROLS
          </div>
          <div className="mt-1 flex items-baseline">
            <div className="text-2xl font-semibold text-green-600">
              {metrics.implementedControls}
            </div>
            <div className="ml-2 text-sm text-gray-600">
              total
            </div>
          </div>
        </div>
        
        <div className="px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            MISSING CONTROLS
          </div>
          <div className="mt-1 flex items-baseline">
            <div className="text-2xl font-semibold text-red-600">
              {metrics.missingControls}
            </div>
            <div className="ml-2 text-sm text-gray-600">
              needed
            </div>
          </div>
        </div>
        
        <div className="px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            ASSETS ASSESSED
          </div>
          <div className="mt-1 flex items-baseline">
            <div className="text-2xl font-semibold text-indigo-600">
              {assets.length}
            </div>
            <div className="ml-2 text-sm text-gray-600">
              systems
            </div>
          </div>
        </div>
      </div>
      
      {/* Heat Map Table - PRD v19: Improved performance with 6 columns (reduced from 9) */}
      {/* PRD v20: Sticky Headers Implementation */}
      <div className="control-heatmap-container" style={{ height: '600px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <table className="min-w-full divide-y divide-gray-200 control-heatmap-table">
          <thead className="sticky-header" style={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 10, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <tr>
              <th scope="col" className="asset-column-header" style={{
                padding: '12px 16px',
                fontWeight: '600',
                fontSize: '11px',
                color: 'white',
                textAlign: 'left',
                borderRight: '1px solid rgba(255,255,255,0.2)',
                minWidth: '200px'
              }}>
                ASSET
              </th>
              {controls.map(control => (
                <th key={control.id} scope="col" className="control-column-header" style={{
                  padding: '12px 8px',
                  fontWeight: '600',
                  fontSize: '11px',
                  color: 'white',
                  textAlign: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.2)',
                  minWidth: '100px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  {control.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedAssets.map(asset => {
              const riskLevel = getAssetRiskClass(asset);
              
              return (
                <tr key={asset.id}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center cursor-pointer" onClick={() => handleCellClick(asset.id, 'assetName')}>
                      {riskLevel === 'high' && (
                        <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      )}
                      {riskLevel === 'medium' && (
                        <span className="inline-block w-2 h-2 bg-orange-400 rounded-full mr-2"></span>
                      )}
                      {riskLevel === 'low' && (
                        <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                      )}
                      <button 
                        className="text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:underline"
                      >
                        {asset.assetName}
                      </button>
                    </div>
                  </td>
                  
                  {controls.map(control => {
                    const value = asset[control.id] || 'Unknown';
                    let cellClass = '';
                    
                    if (value === 'Yes') {
                      cellClass = 'bg-green-100 text-green-800 font-medium';
                    } else if (value === 'No') {
                      cellClass = 'bg-red-100 text-red-800 font-medium';
                    } else if (value === 'N/A') {
                      cellClass = 'bg-gray-100 text-gray-600';
                    }
                    
                    return (
                      <td 
                        key={`${asset.id}-${control.id}`}
                        className={`px-2 py-2 text-center ${cellClass}`}
                        onClick={() => handleCellClick(asset.id, control.id)}
                        style={{ cursor: 'pointer' }}
                        title={`Click to edit ${control.label} for ${asset.assetName || asset.basicInfo?.name}`}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Legend - simplified to only show control status */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center">
          <h4 className="text-sm font-medium text-gray-700 mr-4">Control Status:</h4>
          <div className="flex items-center">
            <span className="inline-block w-4 h-4 bg-green-100 mr-1 border border-green-200"></span>
            <span className="text-xs text-gray-600 mr-3">Yes</span>
            <span className="inline-block w-4 h-4 bg-red-100 mr-1 border border-red-200"></span>
            <span className="text-xs text-gray-600 mr-3">No</span>
            <span className="inline-block w-4 h-4 bg-gray-100 mr-1 border border-gray-200"></span>
            <span className="text-xs text-gray-600">N/A</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyControlsHeatMap; 